import { getDomains } from '@/helpers/getDomains';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import { useEffect, useRef } from 'react';

import { t2medBridge } from '../api/bridge';

/** ml-scribe origin (scheme + host, no version path) for the Rust plugin. */
function getCloudOrigin(): string {
  const { baseV2Url } = getDomains();
  const result = /^(https?:\/\/[^/]+)/.exec(baseV2Url);
  return result?.[1] ?? baseV2Url;
}

/**
 * Pushes the Heidi cloud auth context into `tauri-plugin-t2med`.
 *
 * The plugin owns the writeback poller and launch verification but has no
 * auth SDK — the FE is authoritative for the JWT and mirrors it down on
 * login and every refresh. On logout the plugin also drops all T2med
 * launch state (APS secrets, pending ACKs), so an account switch cannot
 * revive the previous user's T2med session.
 *
 * A cold-start deep link (Heidi launched *from* T2med while closed) is
 * buffered Rust-side and replayed automatically once this hook pushes the
 * first token — no extra handling needed here.
 */
export function useT2medAuthBridge(): void {
  const token = useAuthToken();
  const hadTokenRef = useRef(false);

  useEffect(() => {
    if (token) {
      hadTokenRef.current = true;
      // Order-independent Rust-side: the buffered launch replays once
      // both the base URL and token are present.
      void t2medBridge.setCloudBaseUrl(getCloudOrigin());
      void t2medBridge.setCloudAuthToken(token);
      return;
    }
    // Only clear on a real login → logout transition — not on the initial
    // mount before auth hydrates, which would wipe nothing anyway but
    // also races the plugin's cold-start launch buffering.
    if (hadTokenRef.current) {
      hadTokenRef.current = false;
      void t2medBridge.clearCloudAuthToken();
    }
  }, [token]);
}
